"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  FiSearch,
  FiUser,
  FiShoppingBag,
  FiHeart,
  FiHome,
  FiGrid,
  FiTag,
  FiLink,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

/* ================= ICON MAPPING LOGIC ================= */

const getEcommerceIcon = (text: string) => {
  const label = text.toLowerCase();
  if (label.includes("home")) return <FiHome />;
  if (label.includes("shop") || label.includes("store")) return <FiShoppingBag />;
  if (label.includes("sale") || label.includes("offer")) return <FiTag />;
  if (label.includes("cat") || label.includes("menu")) return <FiGrid />;
  if (label.includes("wish") || label.includes("heart")) return <FiHeart />;
  if (label.includes("user") || label.includes("profile") || label.includes("account")) return <FiUser />;
  return <FiLink />; // Fallback SVG
};

/* ================= COMPONENT ================= */

export default function Navbar_FloatingGlass_Vertical({
  links,
  logoText,
}: {
  links: { id: number; url: string; text: string }[];
  logoText?: string;
}) {
  const pathname = usePathname();
  const [isExpanded, setIsExpanded] = useState(false);

  const actionButtons = [
    { icon: FiSearch, text: "Search" },
    { icon: FiShoppingBag, text: "Cart" },
    { icon: FiUser, text: "Account" },
  ];

  return (
    <motion.aside
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      initial={{ x: -20, opacity: 0 }}
      animate={{ 
        x: 0, 
        opacity: 1,
        width: isExpanded ? 200 : 64 
      }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="
        fixed top-1/2 left-6 -translate-y-1/2 z-50
        flex flex-col
        backdrop-blur-xl bg-white/10 
        border border-white/20
        shadow-[0_8px_32px_0_rgba(0,0,0,0.2)]
        rounded-[2rem] overflow-hidden
      "
    >
      <div className="flex flex-col h-full py-6 px-3">
        
        {/* ================= LOGO ================= */}
        <div className="mb-8 px-3 h-8 flex items-center overflow-hidden">
          <AnimatePresence mode="wait">
            {isExpanded ? (
              <motion.span
                key="logo-text"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="text-xs font-black tracking-widest uppercase text-white whitespace-nowrap"
              >
                {logoText || "Glass"}
              </motion.span>
            ) : (
              <motion.div
                key="logo-dot"
                className="w-2 h-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]"
              />
            )}
          </AnimatePresence>
        </div>

        {/* ================= NAV LINKS (AUTO-SVG) ================= */}
        <nav className="flex flex-col gap-2">
          {links.map((link) => {
            const isActive = pathname === link.url;
            return (
              <Link
                key={link.id}
                href={link.url}
                className={`
                  group relative flex items-center h-11 px-3 rounded-2xl
                  transition-all duration-300
                  ${isActive ? "text-white" : "text-white/50 hover:text-white hover:bg-white/5"}
                `}
              >
                {/* Active Indicator */}
                {isActive && (
                  <motion.div
                    layoutId="active-bg"
                    className="absolute inset-0 bg-white/20 rounded-2xl -z-10"
                    transition={{ type: "spring", bounce: 0.2 }}
                  />
                )}

                <span className="text-xl min-w-[24px]">
                  {getEcommerceIcon(link.text)}
                </span>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="ml-4 text-sm font-medium whitespace-nowrap"
                    >
                      {link.text}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            );
          })}
        </nav>

        {/* ================= DIVIDER ================= */}
        <div className="my-6 mx-3 h-px bg-white/10" />

        {/* ================= ACTIONS ================= */}
        <div className="mt-auto flex flex-col gap-2">
          {actionButtons.map((btn, idx) => (
            <motion.button
              key={idx}
              whileHover={{ x: 2 }}
              className="flex items-center h-11 px-3 rounded-2xl text-white/50 hover:text-white transition-colors"
            >
              <span className="text-xl min-w-[24px]"><btn.icon /></span>
              {isExpanded && (
                <span className="ml-4 text-sm font-medium whitespace-nowrap">
                  {btn.text}
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </motion.aside>
  );
}